/**
 * Students API Hooks - Sophisticated pattern with TanStack Query
 *
 * Provides type-safe API operations for Students with proper error handling,
 * caching, and validation following the established architecture pattern.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type {
  StudentCreate,
  StudentCreateInput,
  StudentFilters,
  StudentListResponse,
  StudentResponse,
  StudentUpdate,
  StudentUpdateInput,
} from "@/types/student"
import { unwrapStandardResponse } from "@/utils/apiResponse"

import { useApiMutation } from "./useApiMutation"

/**
 * Fetch paginated list of students with permission checks
 */
export function useStudents(filters: StudentFilters = {}) {
  const { hasPermission } = usePermissions()

  const hasPermissions =
    hasPermission("students:read") ||
    hasPermission("students:read_own") ||
    hasPermission("students:read_assigned")

  // Allow caller to disable query via enabled filter (defaults to true)
  const isEnabled = filters.enabled !== false && hasPermissions

  return useQuery({
    queryKey: ["students", filters],
    queryFn: async (): Promise<StudentListResponse> => {
      const response = await api.students.readStudentsApiV1StudentsGet({
        limit: filters.limit,
        skip: filters.skip,
        search: filters.search,
        sortBy: filters.sort_by,
        sortOrder: filters.sort_order,
        createdBy: filters.created_by,
        createdAfter: filters.created_after,
        createdBefore: filters.created_before,
        updatedAfter: filters.updated_after,
        updatedBefore: filters.updated_before,
      })

      return unwrapStandardResponse<StudentListResponse>(
        response,
      ) as StudentListResponse
    },
    enabled: isEnabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single student by ID with permission checks
 */
export function useStudent(studentId: string) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["students", studentId],
    queryFn: async (): Promise<StudentResponse> => {
      const response = await api.students.readStudentApiV1StudentsStudentIdGet({
        studentId: studentId,
      })

      return unwrapStandardResponse<StudentResponse>(
        response,
      ) as StudentResponse
    },
    enabled:
      !!studentId &&
      (hasPermission("students:read") ||
        hasPermission("students:read_own") ||
        hasPermission("students:read_assigned")),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Create new student with validation and error handling
 */
export function useCreateStudent() {
  return useApiMutation<StudentResponse, unknown, StudentCreateInput>({
    mutationFn: async (studentData) => {
      // Let the backend handle permission checks

      const studentCreate: StudentCreate = {
        first_name: studentData.first_name,
        last_name: studentData.last_name,
        date_of_birth: studentData.date_of_birth,
        gender: studentData.gender ?? null,
        household_id: studentData.household_id,
        current_swim_level: studentData.current_swim_level ?? null,
        skill_level_notes: studentData.skill_level_notes ?? null,
        has_asthma: studentData.has_asthma ?? null,
        preferred_location: studentData.preferred_location ?? null,
        notes: studentData.notes ?? null,
        is_active: studentData.is_active ?? null,
        photo_url: studentData.photo_url ?? null,
      }

      const response = await api.students.createStudentApiV1StudentsPost({
        requestBody: studentCreate,
      })

      return unwrapStandardResponse<StudentResponse>(
        response,
      ) as StudentResponse
    },
    invalidateQueries: [["students"]],
    successToast: (data) => ({
      title: "Student created successfully",
      description: ` "${data.id}" has been created.`,
    }),
  })
}

/**
 * Update existing student with validation and error handling
 */
export function useUpdateStudent() {
  return useApiMutation<
    StudentResponse,
    unknown,
    { studentId: string; studentData: StudentUpdateInput }
  >({
    mutationFn: async ({
      studentId,
      studentData,
    }: {
      studentId: string
      studentData: StudentUpdateInput
    }) => {
      // Let the backend handle permission checks

      const studentUpdate: StudentUpdate = {
        first_name: studentData.first_name,
        last_name: studentData.last_name,
        date_of_birth: studentData.date_of_birth,
        gender: studentData.gender,
        household_id: studentData.household_id,
        current_swim_level: studentData.current_swim_level,
        skill_level_notes: studentData.skill_level_notes,
        has_asthma: studentData.has_asthma,
        preferred_location: studentData.preferred_location,
        notes: studentData.notes,
        is_active: studentData.is_active,
        photo_url: studentData.photo_url,
      }

      const response =
        await api.students.updateStudentApiV1StudentsStudentIdPut({
          studentId: studentId,
          requestBody: studentUpdate,
        })

      return unwrapStandardResponse<StudentResponse>(
        response,
      ) as StudentResponse
    },
    setQueryData: [
      {
        queryKey: (variables) => ["students", variables.studentId],
        updater: (_previous, data) => data,
      },
    ],
    invalidateQueries: [["students"]],
  })
}

/**
 * Delete student with permission checks and error handling
 */
export function useDeleteStudent() {
  return useApiMutation<{ studentId: string }, unknown, string>({
    mutationFn: async (studentId) => {
      // Let the backend handle permission checks
      await api.students.deleteStudentApiV1StudentsStudentIdDelete({
        studentId: studentId,
      })
      return { studentId }
    },
    removeQueries: [(variables) => ["students", variables]],
    invalidateQueries: [["students"]],
  })
}

/**
 * Invalidate students queries to force a refetch
 */
export function useInvalidateStudents() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({ queryKey: ["students"] })
  }
}
